<script>
import { h } from "vue";
import SecondaryPageLayout from "@/Layouts/SecondaryPageLayout.vue";

export default {
    layout: (_, page) =>
        h(
            SecondaryPageLayout,
            {
                title: "Easy on-the-go gift management",
                titleClass: "max-w-[432px]",
                showHeroArt: false,
            },
            () => page,
        ),
};
</script>

<script setup>
import { Head } from "@inertiajs/vue3";

const BADGE_BASE_URL =
    "https://res.cloudinary.com/gift-hero/image/upload/v1685819255/lib/img/mobile-app";

const stores = [
    {
        href: "https://itunes.apple.com/us/app/gift-hero/id1330224345?ls=1&mt=8",
        title: "Download for iOS",
        badge: `${BADGE_BASE_URL}/badge-ios-app-store.svg`,
        alt: "Available on the App Store",
    },
    {
        href: "https://play.google.com/store/apps/details?id=com.giftheroapp",
        title: "Download for Android",
        badge: `${BADGE_BASE_URL}/badge-google-play.svg`,
        alt: "Get it on Google Play",
    },
];
</script>

<template>
    <Head>
        <title>Gift Hero Mobile App for iOS and Android</title>
        <meta
            name="description"
            content="Add anything from your browser or app to a Gift Hero wish list with the free Add to Wish List app for iOS and Android."
        />
        <meta property="og:title" content="Gift Hero Mobile App" />
        <meta
            property="og:description"
            content="Adding to your wish lists has never been easier."
        />
        <meta property="og:url" :content="route('mobile')" />
        <link rel="canonical" :href="route('mobile')" />
        <meta name="apple-itunes-app" content="app-id=1330224345" />
    </Head>

    <!--
        The phones (flat Figma export, 589×696 @1x) rise 9px above the 171px
        band and hang over the white card, so from lg they sit absolutely
        against the column (which starts 80px below the band) and the wrapper
        reserves their height; they shrink and tuck in at lg/xl so they stay
        inside the card. Below lg they follow the copy in flow.
    -->
    <div class="relative lg:min-h-[331px] xl:min-h-[378px] 2xl:min-h-[436px]">
        <div class="max-w-[524px]">
            <h2
                class="font-brand-display text-[36px] font-black italic uppercase leading-[1.15] tracking-[-0.02em] text-[#102a3d] md:text-[42px]"
            >
                Adding to Your Wish Lists has Never Been Easier.
            </h2>
            <p
                class="mb-0 mt-4 text-[18px] leading-[1.5] tracking-[-0.02em] text-[#102a3d] md:text-[20px]"
            >
                Quickly add anything from your web browser or app to one of your
                existing wish lists or even create a new one thanks to Gift
                Hero’s free Add to Wish List app, now available for iOS and
                Android.
            </p>
            <ul class="mb-0 mt-9 flex list-none flex-wrap gap-7 p-0">
                <li v-for="store in stores" :key="store.href">
                    <a
                        :href="store.href"
                        :title="store.title"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="block transition hover:opacity-80"
                    >
                        <img
                            :src="store.badge"
                            :alt="store.alt"
                            width="139"
                            height="47"
                            class="h-[47px] w-[139px]"
                        />
                    </a>
                </li>
            </ul>
        </div>

        <img
            :src="asset('images/brand/mobile-app-phones.webp')"
            alt="The Gift Hero app on two phones: a wish list and the home screen"
            width="589"
            height="696"
            class="mx-auto mt-10 h-auto w-full max-w-[480px] lg:absolute lg:-top-[260px] lg:right-[-60px] lg:mt-0 lg:w-[500px] lg:max-w-none xl:right-[-100px] xl:w-[540px] 2xl:right-[-178px] 2xl:w-[589px]"
        />
    </div>
</template>
